/** Measured heights of one shipping unit on the letter sheet, in CSS px. */
export interface UnitHeights {
  head: number
  lines: number[]
}

/** One unit's slice on one sheet. `continued` repeats the unit header on a later sheet. */
export interface SheetSegment {
  unitIndex: number
  lines: number[]
  continued: boolean
}

interface PaginateOptions {
  units: UnitHeights[]
  /** Room for units on every sheet: page height minus the repeated header and footer row. */
  capacity: number
  /** Padding + rule each unit block adds around its rows. */
  framePx: number
  /** The signature lines, printed only at the foot of the last sheet. */
  signature: number
}

/**
 * Splits a letter packing list into sheets so each one can repeat the order header and
 * carry `Page x / N`. A unit header never ends a sheet on its own — it moves on with its
 * first line — and a unit split across sheets repeats its header on the next. A row taller
 * than a whole sheet still gets a sheet of its own rather than looping.
 */
export function paginateSheets({ units, capacity, framePx, signature }: PaginateOptions): SheetSegment[][] {
  const pages: SheetSegment[][] = [[]]
  let used = 0

  const startSegment = (unitIndex: number, continued: boolean) => {
    const segment: SheetSegment = { unitIndex, lines: [], continued }
    pages[pages.length - 1].push(segment)
    used += framePx + units[unitIndex].head
    return segment
  }

  const newPage = () => {
    pages.push([])
    used = 0
  }

  units.forEach((unit, unitIndex) => {
    // Header plus its first line must fit together.
    if (used > 0 && used + framePx + unit.head + (unit.lines[0] ?? 0) > capacity) newPage()
    let segment = startSegment(unitIndex, false)

    unit.lines.forEach((height, lineIndex) => {
      if (segment.lines.length > 0 && used + height > capacity) {
        newPage()
        segment = startSegment(unitIndex, true)
      }
      segment.lines.push(lineIndex)
      used += height
    })
  })

  if (used > 0 && used + signature > capacity) newPage()
  return pages
}
